import { Platform } from 'react-native'
import * as SecureStore from 'expo-secure-store'

/**
 * Cross-platform persistence for the session token blob.
 *
 * - Native (iOS/Android): expo-secure-store (Keychain / Keystore).
 * - Web: `sessionStorage`. expo-secure-store has **no web backend** (its web module
 *   is an empty object), so calling it on web throws and the session would be lost
 *   on every reload. We persist to `sessionStorage` — not `localStorage` — so the
 *   token blob is dropped when the tab closes, mirroring the old `as-life-web`
 *   (oidc-client used `WebStorageStateStore({ store: sessionStorage })`) and
 *   limiting XSS exposure of the refresh token.
 *
 * `typeof window` is guarded so static prerender (expo `output: static`, runs in
 * Node) doesn't crash.
 */
const webStore: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null =
  typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null

export const tokenStore = {
  async getItem(key: string): Promise<string | null> {
    if (Platform.OS === 'web') return webStore?.getItem(key) ?? null
    return SecureStore.getItemAsync(key)
  },
  async setItem(key: string, value: string): Promise<void> {
    if (Platform.OS === 'web') {
      webStore?.setItem(key, value)
      return
    }
    await SecureStore.setItemAsync(key, value)
  },
  async deleteItem(key: string): Promise<void> {
    if (Platform.OS === 'web') {
      webStore?.removeItem(key)
      return
    }
    await SecureStore.deleteItemAsync(key)
  },
}
